// ---------------------------------------------------------------------------
// carplayBridge — JS side of the CarPlay nav-state channel.
//
// The native side (ios/App/App/CarPlay/) renders the car screen from the
// state pushed here. Everything is fire-and-forget and silently no-ops on
// web / Android / iPhones without a car connected — navigation never
// depends on the car screen.
//
// Lifecycle contract (mirrors NavigationView):
//   carplayStartNav(...)        — once, when navigation opens
//   carplayUpdateProgress(...)  — each nav tick (throttled to ~1/s here)
//   carplayUpdateRoute(...)     — after an off-route recalculation
//   carplayEndNav()             — when navigation closes or arrives
// ---------------------------------------------------------------------------

import { Capacitor, registerPlugin } from "@capacitor/core";
import type { LngLat } from "@/utils/polyline";
import { computeRouteDetour } from "@/components/DriveByOptions";
import { simplifyRoadName } from "@/utils/roadNames";
import { fmtDistance } from "@/utils/units";
import { markCarplayCardEligible } from "@/utils/carplayCardGate";

interface CarPlayBridgePlugin {
  startNavigation(opts: {
    destinationName: string;
    pleasantReason: string;
    coordinates: number[];
  }): Promise<void>;
  updateProgress(opts: {
    instruction: string;
    distanceToManeuverM: number;
    symbolName: string;
    remainingMeters: number;
    remainingSeconds: number;
  }): Promise<void>;
  updateRoute(opts: { coordinates: number[]; label?: string }): Promise<void>;
  chime(): Promise<void>;
  stopPendingRecommend(opts: { id: string; name: string; lat: number; lng: number }): Promise<void>;
  endNavigation(): Promise<void>;
  isCarConnected(): Promise<{ connected: boolean }>;
  heartbeat(opts: { seq: number; rttMs: number }): Promise<void>;
  setMapConfig(opts: { rasterTileURLTemplate: string; attribution: string }): Promise<void>;
  setDestinations(opts: { destinations: Array<Record<string, unknown>> }): Promise<void>;
  setRouteChoices(opts: { final?: boolean; destinationName: string; destinationPlace?: string; choices: Array<Record<string, unknown>> }): Promise<void>;
  clearRouteChoices(): Promise<void>;
  setSearchResults(opts: { seq: number; results: Array<Record<string, unknown>> }): Promise<void>;
  speakGuidance(opts: { text: string; volume: number }): Promise<void>;
  startDictation(): Promise<{ text?: string; denied?: boolean; unavailable?: boolean }>;
  primeVoicePermissions(): Promise<{ speech: boolean; microphone: boolean; prompted: boolean }>;
  showCarStatus(opts: { text: string }): Promise<void>;
  showFerryAlert(opts: { title: string }): Promise<void>;
  setDirections(opts: { items: Array<{ instruction: string; detail: string }> }): Promise<void>;
  stopGuidance(): Promise<void>;
  arrived(opts: { summary: string }): Promise<void>;
  lovedRoad(): Promise<void>;
  addListener(
    eventName: string,
    cb: (data: Record<string, unknown>) => void,
  ): Promise<{ remove: () => void }>;
}

const CarPlayBridge = registerPlugin<CarPlayBridgePlugin>("CarPlayBridge");

// ---------------------------------------------------------------------------
// STAGE 0 (CarPlay reliability gate — v2 architecture review):
//  1. Heartbeat: 1s pings native-ward while a car is connected, carrying the
//     previous beat's round-trip time. Native logs gaps — this is the DATA
//     that decides whether the WebView survives phone-lock, or the nav
//     runtime moves fully to Swift.
//  2. Web-speech gate: "WebView speech is disabled whenever CarPlay is
//     connected. One native speech controller owns guidance audio." Until
//     the native controller ships, car drives are banner-only by design.
// ---------------------------------------------------------------------------

let carConnected = false;
let hbTimer: ReturnType<typeof setInterval> | null = null;
let hbSeq = 0;
let hbLastRtt = 0;

/** True while a CarPlay screen is attached. NavigationView's speak() checks
 *  this — the phone's web speech goes silent the moment the car connects. */
export function isCarplayConnected(): boolean {
  return carConnected;
}

/** Speak a guidance cue through the native controller (audio spec #6:
 *  per-utterance session, ducks music, releases immediately). iOS routes
 *  it to the car speakers when CarPlay is connected. Fire-and-forget. */
/** Phone dictation (Morgan's friction batch): native ears for the phone's
 *  search mic. Resolves the transcript, "" when nothing was heard, or
 *  null when unavailable/denied — caller decides how to nudge. */
export async function phoneDictation(): Promise<string | null> {
  try {
    const r = await CarPlayBridge.startDictation();
    if (r.denied || r.unavailable) return null;
    return (r.text ?? "").trim();
  } catch {
    return null;
  }
}

/** Ask for Speech Recognition + Microphone while the phone is in the user's
 *  hand, so the car never has to.
 *
 *  iOS always shows these prompts on the PHONE — never on the CarPlay screen.
 *  Tapping the car's mic with permissions undetermined therefore looks like a
 *  dead button: the alert is on a phone in a pocket, and nothing happens on
 *  the dash until it's answered. Calling this once from the phone removes that
 *  moment entirely, which matters because the alternative is asking someone to
 *  pick up their phone while driving.
 *
 *  Safe to call repeatedly and on any platform — resolves all-false off iOS,
 *  and iOS returns an existing decision without re-prompting. */
export async function primeVoicePermissions(): Promise<{
  speech: boolean;
  microphone: boolean;
  prompted: boolean;
}> {
  const off = { speech: false, microphone: false, prompted: false };
  if (!isIosEarly()) return off;
  try {
    return await CarPlayBridge.primeVoicePermissions();
  } catch {
    return off;
  }
}

/** Answer the car's Directions button with the highlighted route's turns. */
export function carplayPushDirections(
  items: Array<{ instruction: string; detail: string }>,
): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.setDirections({ items: items.slice(0, 60) }).catch(() => {});
}

/** Spoken AND written (Morgan: "we also need to have it written across
 *  the screen in case there's no audio on") — service/status alerts go
 *  through here so the car shows the words while saying them. */
/// `screenText` — say the long version, SHOW the short one. A sentence that
/// reads naturally aloud is usually too long for a glance at 55mph, and the
/// car banner cut Morgan's long-trip notice mid-word (Aug 1). Speech has no
/// width limit; the screen does. Defaults to `text` when they're the same.
export function carplayAnnounce(text: string, volume = 1.0, screenText?: string): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.showCarStatus({ text: screenText ?? text }).catch(() => {});
  void CarPlayBridge.speakGuidance({ text, volume }).catch(() => {});
}

/**
 * PERSISTENT RETRY (Morgan: "we need to be searching constantly until
 * there is service while saving the requested trip and then when there
 * is service, tell the user"). One pending car request survives the dead
 * zone: retried every 20s AND on the online event; on the first attempt
 * with connectivity, the car announces "There's service now — searching
 * your route" and the original request re-fires exactly as asked.
 */
let pendingCarRequest: { data: Record<string, unknown>; label: string } | null = null;
let pendingRetryTimer: ReturnType<typeof setInterval> | null = null;

function queueCarRequest(data: Record<string, unknown>, label: string): void {
  pendingCarRequest = { data, label };
  if (pendingRetryTimer) clearInterval(pendingRetryTimer);
  pendingRetryTimer = setInterval(tryPendingCarRequest, 20_000);
  window.addEventListener("online", tryPendingCarRequest);
}

function tryPendingCarRequest(): void {
  const pending = pendingCarRequest;
  if (!pending) {
    if (pendingRetryTimer) { clearInterval(pendingRetryTimer); pendingRetryTimer = null; }
    window.removeEventListener("online", tryPendingCarRequest);
    return;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) return; // still dark
  pendingCarRequest = null;
  if (pendingRetryTimer) { clearInterval(pendingRetryTimer); pendingRetryTimer = null; }
  window.removeEventListener("online", tryPendingCarRequest);
  carplayAnnounce(`There's service now — searching ${pending.label}.`);
  handleCarRequest(pending.data);
}

export function carplaySpeak(text: string, volume: number): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.speakGuidance({ text, volume }).catch(() => {});
}

export function carplayStopSpeech(): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.stopGuidance().catch(() => {});
}

function startHeartbeat(): void {
  if (hbTimer) return;
  hbTimer = setInterval(() => {
    const t0 = performance.now();
    hbSeq += 1;
    void CarPlayBridge.heartbeat({ seq: hbSeq, rttMs: hbLastRtt })
      .then(() => { hbLastRtt = Math.round(performance.now() - t0); })
      .catch(() => {});
  }, 1000);
}

function stopHeartbeat(): void {
  if (hbTimer) { clearInterval(hbTimer); hbTimer = null; }
}

// ---------------------------------------------------------------------------
// STAGE 2 — the car chooses too. The car screen asks; this module answers
// from the app's own data (saved places, trip history) and relays trip
// requests into the app via window events that Index.tsx handles. The JS
// engine stays the only routing brain.
// ---------------------------------------------------------------------------

/** id → destination details, kept between the list request and the pick.
 *  Search results may lack coords until picked (Google resolves lazily). */
const carDestCache = new Map<string, {
  name: string;
  place: string;
  coords: LngLat | null;
  sugg?: { id: string; name: string; place: string; center: LngLat | null };
}>();
let carAwaitingChoices = false;
/** One resume offer per connection — never a nagging loop. */
let resumeOffered = false;
/** Signature of the last pushed choice set — dedupes identical re-pushes. */
let carLastChoicesSig = "";
/** When the car asked for routes (ms epoch) — drives the timing log. */
let carRoutesRequestedAt = 0;

/** Index.tsx calls this when classified cards are ready AND the car asked. */
export function carWantsRouteChoices(): boolean {
  return carAwaitingChoices;
}

/** MIRROR THE PHONE'S TRIP TO THE DASH (Morgan, Aug 1: "when i do route entry
 *  on my phone it does not come up in carplay — but that is something you can
 *  do with other navigation").
 *
 *  `carAwaitingChoices` only ever goes true when the CAR asks — a destination
 *  picked on the car screen, or a car loop request. A trip planned on the
 *  phone therefore never reached the dash at all; the car sat empty until GO,
 *  when ensureNavSession synthesised a trip for the system chrome. Every other
 *  navigation app mirrors the plan, not just the drive.
 *
 *  So: show the car the phone's cards whenever a car is attached — unless it's
 *  already navigating, where replacing the live trip preview would be worse
 *  than showing nothing. */
export function carShouldShowTrip(): boolean {
  if (carAwaitingChoices) return true;
  return carConnected && !carNavActive;
}

/** SEND TO CARPLAY — an explicit button beats invisible mirroring.
 *
 *  Morgan, Aug 1: "there should be a button when i am attached to carplay on
 *  my phone that says share with carplay so that i can make routes happen that
 *  way." She's right, and it's better than what I built first: automatic
 *  mirroring is silent when it fails, so you can't tell a broken bridge from a
 *  car that simply hasn't been told anything. A button either works or
 *  visibly doesn't.
 *
 *  Clears the dedupe signature so the same trip can be re-sent deliberately —
 *  pressing it twice should push twice, because the user pressing again means
 *  "it didn't work, try again". */
export function carplaySendCurrentTrip(): void {
  carLastChoicesSig = "";
  carRoutesRequestedAt = Date.now();
}

export function carplayPushRouteChoices(args: {
  destinationName: string;
  /** Generation is DONE — this set is the whole answer. The car stops
   *  saying "still looking" (Morgan, Aug 7). */
  final?: boolean;
  /** Town/context line for the preview card ("Sleepy Hollow, NY 10591").
   *  Empty when it would just repeat the name. */
  destinationPlace?: string;
  choices: Array<{
    classification: string;
    title: string;
    reason: string;
    etaSeconds: number;
    distanceMeters?: number;
    extraSeconds: number;
    coordinates: LngLat[];
    selected: boolean;
  }>;
}): void {
  if (!isIosEarly()) return;
  // (Morgan: "there should be a scenic route always") STAY SUBSCRIBED.
  // The Fastest card is computed first; Pleasant/Scenic finish scoring a
  // few seconds later. The old one-shot (carAwaitingChoices = false here)
  // froze the car on that first fastest-only snapshot and threw away the
  // full set when it landed. Now the car keeps receiving updates until a
  // route is picked or the trip is cancelled — mirroring the phone's
  // progressive cards. The signature check stops identical re-pushes from
  // flickering Apple's preview panel.
  const sig =
    args.destinationName +
    "|" +
    args.choices
      .map((c) => `${c.classification}:${Math.round(c.etaSeconds)}`)
      .join("|");
  if (sig === carLastChoicesSig) return;
  carLastChoicesSig = sig;
  const t = carRoutesRequestedAt;
  if (t > 0) {
    // Timing instrumentation (Morgan: "taking way too long to find routes")
    // — visible in the Xcode console as ⚡️ console.log lines.
    console.log(
      `PLEASANT CAR TIMING: ${((Date.now() - t) / 1000).toFixed(1)}s from destination pick → ` +
        `${args.choices.length} card(s): ${args.choices.map((c) => c.classification).join(", ")}`,
    );
  }
  void CarPlayBridge.setRouteChoices({
    final: !!args.final,
    destinationName: args.destinationName,
    destinationPlace: args.destinationPlace ?? "",
    choices: args.choices.map((c) => ({
      classification: c.classification,
      title: c.title,
      reason: c.reason,
      etaSeconds: c.etaSeconds,
      distanceMeters: c.distanceMeters ?? 0,
      extraSeconds: c.extraSeconds,
      selected: c.selected,
      coordinates: flatten(c.coordinates),
    })),
  }).catch(() => {});
}

async function answerDestinationsRequest(): Promise<void> {
  const [{ loadSavedPlaces }, { listTrips }, { loadRecentSearches }] = await Promise.all([
    import("@/utils/savedPlaces"),
    import("@/utils/tripHistory"),
    import("@/utils/recentSearches"),
  ]);
  carDestCache.clear();
  const out: Array<{ id: string; title: string; subtitle: string; kind: string }> = [];
  for (const p of loadSavedPlaces()) {
    const id = `fav:${p.id}`;
    carDestCache.set(id, { name: p.name, place: p.place, coords: p.coords });
    // Labeled places get VIP ordering on the car (Morgan: "save HOME WORK
    // SCHOOL etc") — home/work/school/gym lead, everything else follows.
    const kind = /home/i.test(p.label) ? "home"
      : /work/i.test(p.label) ? "work"
      : /school/i.test(p.label) ? "school"
      : /gym/i.test(p.label) ? "gym"
      : "favorite";
    out.push({ id, title: p.label, subtitle: p.name, kind });
  }
  // Recents = places you SEARCHED plus trips you finished, newest first.
  //
  // Morgan, Aug 1: "Where to?" said "No saved places yet" on a phone that had
  // just routed to Boston and Toronto. This list only ever read trip history,
  // and a trip is only recorded on ARRIVAL — previewing routes, or driving
  // most of the way and stopping, leaves nothing behind. So the car forgot
  // every destination she'd actually looked up. Searches are the honest
  // signal of "somewhere I was headed"; arrivals alone are too strict.
  const recents: Array<{ id: string; title: string; place: string; coords: LngLat; at: number }> = [];
  for (const t of listTrips().slice(0, 12)) {
    recents.push({
      id: `recent:${t.id}`,
      title: t.destinationName,
      place: t.destinationName,
      coords: t.endCoords,
      at: t.arrivedAt,
    });
  }
  for (const r of loadRecentSearches().slice(0, 12)) {
    recents.push({
      id: `search:${r.id}`,
      title: r.name,
      place: r.place,
      coords: r.coords,
      at: r.usedAt,
    });
  }
  recents.sort((a, b) => b.at - a.at);
  const seen = new Set<string>();
  for (const r of recents) {
    // Same place from a search and from a finished trip is ONE row; whichever
    // is newer wins, since the list is already sorted.
    const key = r.title.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    carDestCache.set(r.id, { name: r.title, place: r.place, coords: r.coords });
    // Subtitle only when it says something the title doesn't ("Boston" /
    // "Boston, MA" would just be noise on a car screen).
    const sub = r.place && r.place.trim().toLowerCase() !== key ? r.place : "";
    out.push({ id: r.id, title: r.title, subtitle: sub, kind: "recent" });
    if (seen.size >= 12) break;
  }
  void CarPlayBridge.setDestinations({ destinations: out }).catch(() => {});
}

/** Last known position, for proximity-biased car search. Cached ~10 min so
 *  we don't hit GPS per keystroke. Without this, "3 Macpherson Avenue" from
 *  the car matched Toronto's — the phone's search biases to the user's
 *  location and the car's must too (Morgan: the pleasant app, just on the
 *  car screen). */
let cachedProximity: { coords: LngLat; ts: number } | null = null;

/** The native pump's most recent fix, with its age — the fallback that
 *  saves a trip start when the webview geolocation plugin times out while
 *  Swift is happily streaming positions (Morgan, Aug 8, Umbria: "other
 *  apps could find my GPS — so it was our fault." It was). */
export function lastPumpFix(): { coords: LngLat; ageMs: number } | null {
  if (!cachedProximity) return null;
  return { coords: cachedProximity.coords, ageMs: Date.now() - cachedProximity.ts };
}
/** NEVER BLOCKS (the "search stopped searching" bug: awaiting the webview's
 *  geolocation could hang forever, and every keystroke waited on it).
 *  Returns the freshest coords we already have — the native pump's fixes
 *  stream in whenever a car is connected, so this is warm within seconds
 *  of plugging in — and kicks a background webview-GPS refresh as backup. */
function currentProximity(): LngLat | undefined {
  const fresh =
    cachedProximity && Date.now() - cachedProximity.ts < 10 * 60 * 1000
      ? cachedProximity.coords
      : undefined;
  if (!fresh && navigator.geolocation) {
    // Background refresh only — the search itself never waits on this.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cachedProximity = { coords: [pos.coords.longitude, pos.coords.latitude], ts: Date.now() };
      },
      () => { /* keep whatever we have */ },
      { maximumAge: 10 * 60 * 1000, timeout: 3000 },
    );
  }
  return fresh ?? cachedProximity?.coords;
}

/** The pump's freshest fix — works with the phone LOCKED (webview
 *  geolocation doesn't). The car destination flow starts from this. */
export function carplayLastFix(): LngLat | null {
  return cachedProximity && Date.now() - cachedProximity.ts < 10 * 60 * 1000
    ? cachedProximity.coords
    : null;
}

/** Freshest motion state (speed m/s + heading) from the pump. */
let lastMotion: { speedMps: number; headingDeg: number | null; ts: number } | null = null;

/**
 * ANTICIPATED ORIGIN (Morgan: "it takes a while to load routes and you
 * are moving — anticipate where they will be on the same road in 30-45
 * seconds"). When the driver is in motion, routes should start from
 * where the car WILL BE when they arrive, not where it was when they
 * asked. Projects ~35s ahead along the current heading, capped at 1.2km
 * so a bad speed reading can't fling the origin down the highway.
 * Stationary or no recent motion data → returns the coords unchanged.
 */
export function anticipateOrigin(
  coords: LngLat,
  motion?: { speedMps: number | null; headingDeg: number | null },
): LngLat {
  const m = motion && motion.speedMps != null
    ? { speedMps: motion.speedMps, headingDeg: motion.headingDeg, ts: Date.now() }
    : lastMotion;
  if (!m || Date.now() - m.ts > 30_000) return coords;
  if (m.speedMps < 4 || m.headingDeg == null) return coords; // < ~9 mph = parked-ish
  const distM = Math.min(m.speedMps * 35, 1_200);
  const R = 6371000;
  const br = (m.headingDeg * Math.PI) / 180;
  const lat1 = (coords[1] * Math.PI) / 180;
  const lng1 = (coords[0] * Math.PI) / 180;
  const d = distM / R;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(br) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
  );
  return [((lng2 * 180) / Math.PI + 540) % 360 - 180, (lat2 * 180) / Math.PI];
}

/** True when the pump says we're moving at driving speed right now. */
export function isMovingNow(): boolean {
  return !!lastMotion && Date.now() - lastMotion.ts < 30_000 && lastMotion.speedMps >= 4;
}

/** NavigationView's answer to a car "detours" request — the same list the
 *  phone's drive-by panel shows, delivered through the search-results
 *  channel; picks route back through the addStop path. */
export function carplayPushDetourResults(
  seq: number,
  options: Array<{ name: string; subtitle: string; coords: LngLat }>,
): void {
  if (!isIosEarly()) return;
  const results = options.slice(0, 10).map((o, i) => {
    const id = `detour:${seq}:${i}`;
    carDestCache.set(id, { name: o.name, place: o.subtitle, coords: o.coords });
    return { id, title: o.name, subtitle: o.subtitle };
  });
  void CarPlayBridge.setSearchResults({ seq, results }).catch(() => {});
}

/** Places the phone ALREADY KNOWS that match the query — saved places and
 *  past trips. Zero network, so they can answer a driving search
 *  instantly at any signal level (Morgan: "have it come back right
 *  away"). Matches are registered in carDestCache so a tap routes. */
async function localDestinationMatches(
  query: string,
): Promise<Array<{ id: string; title: string; subtitle: string }>> {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  try {
    const [{ loadSavedPlaces }, { listTrips }] = await Promise.all([
      import("@/utils/savedPlaces"),
      import("@/utils/tripHistory"),
    ]);
    const out: Array<{ id: string; title: string; subtitle: string }> = [];
    const seen = new Set<string>();
    for (const p of loadSavedPlaces()) {
      if (!`${p.label} ${p.name} ${p.place}`.toLowerCase().includes(q)) continue;
      const key = p.label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const id = `fav:${p.id}`;
      carDestCache.set(id, { name: p.name, place: p.place, coords: p.coords });
      out.push({ id, title: p.label, subtitle: p.name });
    }
    for (const t of listTrips().slice(0, 30)) {
      const key = t.destinationName.toLowerCase();
      if (!key.includes(q) || seen.has(key)) continue;
      seen.add(key);
      const id = `recent:${t.id}`;
      carDestCache.set(id, { name: t.destinationName, place: t.destinationName, coords: t.endCoords });
      out.push({ id, title: t.destinationName, subtitle: "Recent trip" });
    }
    return out.slice(0, 5);
  } catch {
    return [];
  }
}

async function answerSearchRequest(query: string, seq: number): Promise<void> {
  // GREAT ROADS NEAR ME (magic query from the car's Explore row): the
  // curated gems within ~60km of the car, best pleasant scores first,
  // straight from the bundled library — zero network, works in dead zones.
  if (query === "__gems__") {
    try {
      const here = carplayLastFix() ?? currentProximity();
      if (!here) {
        void CarPlayBridge.setSearchResults({
          seq,
          results: [{ id: "nofix", title: "Need your location first", subtitle: "Try again in a few seconds" }],
        }).catch(() => {});
        return;
      }
      const { GREAT_DRIVES } = await import("@/content/greatDrives");
      const km = (a: LngLat, b: LngLat) => {
        const R = 6371;
        const dLat = ((b[1] - a[1]) * Math.PI) / 180;
        const dLng = ((b[0] - a[0]) * Math.PI) / 180;
        const s = Math.sin(dLat / 2) ** 2 +
          Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(s));
      };
      const gems = (GREAT_DRIVES as Array<{
        id: string; name: string; region: string; category: string;
        point: [number, number]; scores: { pleasant: number; scenic: number };
        blurb: string;
      }>)
        .filter((d) => d.category === "great_road" && Array.isArray(d.point))
        .map((d) => ({ d, dist: km(here, d.point) }))
        .filter((x) => x.dist <= 60)
        .sort((a, b) =>
          (b.d.scores.pleasant + b.d.scores.scenic) - (a.d.scores.pleasant + a.d.scores.scenic) ||
          a.dist - b.dist)
        .slice(0, 8);
      const results = gems.map(({ d, dist }) => {
        const id = `gem:${d.id}`;
        const title = d.name.split(":")[0].trim();
        carDestCache.set(id, { name: title, place: d.region, coords: d.point });
        // Driver's chosen units, same as every phone screen (Aug 19 units
        // audit) - this row sits under a system banner that already speaks
        // the local language.
        return { id, title, subtitle: `${fmtDistance(dist * 1000)} · ${d.region}` };
      });
      void CarPlayBridge.setSearchResults({
        seq,
        results: results.length > 0 ? results : [{
          id: "none",
          title: "No hand-picked roads within an hour yet",
          subtitle: "Your ratings help build this area",
        }],
      }).catch(() => {});
    } catch {
      void CarPlayBridge.setSearchResults({ seq, results: [] }).catch(() => {});
    }
    return;
  }
  const { isOffline, raceTimeout } = await import("@/lib/net");
  const push = (results: Array<{ id: string; title: string; subtitle: string }>) =>
    void CarPlayBridge.setSearchResults({ seq, results }).catch(() => {});

  // LOCAL-FIRST (Morgan: mid-drive search must "come back right away"):
  // saved places + past trips matching the words answer with zero network.
  const local = await localDestinationMatches(query);

  if (isOffline()) {
    if (local.length > 0) {
      push([...local, {
        id: "offline",
        title: "No signal — showing places you know",
        subtitle: "Watching for service to search the map",
      }]);
    } else {
      push([{
        id: "offline",
        title: "No signal here",
        subtitle: "Watching for service — I'll search the moment it's back",
      }]);
      carplayAnnounce("No signal — I saved your search and I'm watching for service.");
    }
    queueCarRequest({ type: "search", query, seq }, "the map");
    return;
  }

  const { geocodeAutocomplete } = await import("@/api/routing");
  const proximity = currentProximity();
  const toResults = (suggestions: Awaited<ReturnType<typeof geocodeAutocomplete>>) => {
    const results: Array<{ id: string; title: string; subtitle: string }> = [];
    for (const s of suggestions.slice(0, 8)) {
      const id = `search:${s.id}`;
      carDestCache.set(id, { name: s.name, place: s.place, coords: s.center ?? null, sugg: s });
      // NO star ratings in the car (Morgan, Aug 5: "explore on carplay has
      // these ratings" — the Google divorce applies to the dashboard too).
      // Open/Closed stays: that's the one thing a driver actually needs.
      const open = s.openNow === true ? "Open" : s.openNow === false ? "Closed" : "";
      // DETOUR COST, mid-drive (Morgan, Aug 6): "+7 min · +2.1 mi" — what
      // this stop actually adds to the CURRENT trip. Pure geometry against
      // the live route line; skipped when idle or the result has no coords.
      let cost = "";
      if (navRouteCoords && s.center && (s.center[0] !== 0 || s.center[1] !== 0)) {
        try {
          const d = computeRouteDetour(navRouteCoords, s.center)?.driving;
          if (d && d.addedDurationS >= 0) {
            const min = Math.max(1, Math.round(d.addedDurationS / 60));
            // Driver's chosen units (Aug 19 units audit).
            cost = `+${min} min · +${fmtDistance(d.addedDistanceM)}`;
          }
        } catch { /* cost is a bonus */ }
      }
      const subtitle = [cost, open, s.place ?? ""].filter(Boolean).join(" · ");
      results.push({ id, title: s.name, subtitle });
    }
    return results;
  };
  const merged = (net: Array<{ id: string; title: string; subtitle: string }>) => {
    const titles = new Set(net.map((r) => r.title.toLowerCase()));
    return [...local.filter((l) => !titles.has(l.title.toLowerCase())), ...net].slice(0, 10);
  };

  // Give the network a short head start; past that, the driver sees the
  // local matches immediately and the map results catch up when they land.
  const netPromise = geocodeAutocomplete(query, proximity).catch(() => []);
  const fast = await raceTimeout(netPromise, 1_500);
  if (fast) {
    push(merged(toResults(fast)));
    return;
  }
  if (local.length > 0) {
    push([...local, { id: "searching", title: "Searching the map…", subtitle: "Weak signal — one moment" }]);
  }
  const late = await netPromise;
  const results = toResults(late ?? []);
  if (results.length > 0) push(merged(results));
  else if (local.length > 0) push(local);
  else push([{ id: "nores", title: "Nothing found", subtitle: "Try different words" }]);
}

async function resolveAndSetDestination(dest: NonNullable<ReturnType<typeof carDestCache.get>>): Promise<void> {
  let coords = dest.coords;
  if (!coords && dest.sugg) {
    const { resolveSuggestionCoords } = await import("@/api/routing");
    coords = await resolveSuggestionCoords(dest.sugg as never).catch(() => null);
  }
  if (!coords) return;
  window.dispatchEvent(new CustomEvent("pleasant:car-set-destination", {
    detail: { name: dest.name, place: dest.place, coords },
  }));
}

const CAR_RATINGS_KEY = "pleasant.carRatings.v1";
const CAR_RATINGS_FLUSHED_KEY = "pleasant.carRatings.flushedAt.v1";

/** Car arrival rating -> route_feedback, the SAME table the phone recap's
 *  buttons write, so the admin dashboard sees car and phone ratings alike.
 *  Car stars (Loved it 3 / It was fine 2 / Not pleasant 1) map onto the
 *  recap's 5-good..1-bad scale. submitFeedback handles auth, the offline
 *  queue and its own confession log. */
function submitCarRating(stars: number): void {
  const rating = stars >= 3 ? 5 : stars === 2 ? 3 : 1;
  const label = stars >= 3 ? "loved it" : stars === 2 ? "it was fine" : "not pleasant";
  // Prefer the frozen just-ended drive; fall back to live state for a rating
  // tapped before teardown finishes (Aug 19 audit, A2).
  const nav = completedNav ?? {
    start: lastNavStart,
    end: lastNavEnd,
    classification: lastNavClassification,
    distanceM: lastNavDistanceM,
  };
  void import("@/api/community")
    .then((m) => m.submitFeedback({
      mode: "driving",
      rating,
      tags: [`car arrival: ${label}`],
      routeDistanceM: nav.distanceM,
      start: nav.start ?? undefined,
      end: nav.end ?? undefined,
      classification: nav.classification,
      routeSource: "carplay-nav",
    }))
    // submitFeedback logs retryable failures itself, but a thrown
    // NON-retryable error (schema drift, PGRST204) used to vanish into this
    // catch without a single line - the exact shape of "I rated and nothing
    // appears" (Aug 19 audit, X2). Say what was lost.
    .catch((e) => { console.error("[carplay] rating dropped:", e); });
}

/** ONE-TIME RESCUE (Morgan, Aug 13: "i just routed and said it was good at
 *  the end and nothing appears on the dashboard"). Car ratings used to stop
 *  at the localStorage list below — capped at 100 entries that nothing ever
 *  read. Push any rating from the last 7 days that hasn't been sent yet,
 *  once, at startup; the watermark stops re-sends. */
function flushUnsentCarRatings(): void {
  try {
    const flushedAt = Number(localStorage.getItem(CAR_RATINGS_FLUSHED_KEY) ?? 0);
    const cur = JSON.parse(localStorage.getItem(CAR_RATINGS_KEY) ?? "[]") as Array<{ t: number; stars: number }>;
    const cutoff = Date.now() - 7 * 24 * 3600_000;
    const unsent = cur.filter((r) => r.t > flushedAt && r.t > cutoff);
    if (unsent.length === 0) return;
    localStorage.setItem(CAR_RATINGS_FLUSHED_KEY, String(Date.now()));
    for (const r of unsent) submitCarRating(r.stars);
  } catch { /* best effort */ }
}

function handleCarRequest(data: Record<string, unknown>): void {
  const type = data?.type as string;
  if (type === "destinations") {
    void answerDestinationsRequest();
  } else if (type === "search") {
    void answerSearchRequest(String(data.query ?? ""), Number(data.seq ?? 0));
  } else if (type === "routes") {
    const dest = carDestCache.get(String(data.destinationId ?? ""));
    if (!dest) return;
    // Offline: say so now, retry ourselves when coverage returns — never
    // leave the car on "Searching…" into dead air (Morgan).
    void import("@/lib/net").then(({ isOffline }) => {
      if (isOffline()) {
        carplayAnnounce("No signal — I saved your trip and I'm watching for service.");
        queueCarRequest(data, "your route");
        return;
      }
      carAwaitingChoices = true;
      carLastChoicesSig = "";
      carRoutesRequestedAt = Date.now();
      void resolveAndSetDestination(dest);
    });
  } else if (type === "loop") {
    // Pleasant Loop from the car: no destination — Index builds the loop
    // plan from the freshest fix and runs the normal pipeline; the cards
    // come back to the dashboard like any trip's.
    void import("@/lib/net").then(({ isOffline }) => {
      if (isOffline()) {
        carplayAnnounce("No signal — I saved your loop and I'm watching for service.");
        queueCarRequest(data, "your loop");
        return;
      }
      carAwaitingChoices = true;
      carLastChoicesSig = "";
      carRoutesRequestedAt = Date.now();
      window.dispatchEvent(new CustomEvent("pleasant:car-loop", {
        detail: { minutes: Number(data.minutes ?? 60) },
      }));
    });
  } else if (type === "directions") {
    // Route-preview turn list: Index owns the cards; it answers via
    // carplayPushDirections below.
    window.dispatchEvent(new CustomEvent("pleasant:car-directions", {
      detail: { classification: String(data.classification ?? "pleasant") },
    }));
  } else if (type === "navDirections") {
    // MID-DRIVE turn list (Morgan, Aug 6: "at this page on any given point
    // i want to be able to see the steps"). NavigationView owns the live
    // route; it answers with the remaining steps via carplayPushDirections.
    window.dispatchEvent(new CustomEvent("pleasant:car-nav-directions"));
  } else if (type === "detours") {
    // The car wants the phone's drive-by suggestions for the current
    // route. NavigationView owns the route — it answers via
    // carplayPushDetourResults below.
    window.dispatchEvent(new CustomEvent("pleasant:car-detours", {
      detail: { seq: Number(data.seq ?? 0) },
    }));
  } else if (type === "addStop") {
    // Mid-drive stop (Morgan: "stop for groceries or coffee or gas on the
    // way"): resolve the picked place, hand it to the live navigation.
    const dest = carDestCache.get(String(data.destinationId ?? ""));
    if (!dest) return;
    void (async () => {
      let coords = dest.coords;
      if (!coords && dest.sugg) {
        const { resolveSuggestionCoords } = await import("@/api/routing");
        coords = await resolveSuggestionCoords(dest.sugg as never).catch(() => null);
      }
      if (!coords) return;
      // Tell the car where the stop IS (Morgan, Aug 7): the scene stars it
      // on the polyline and defers the recommend-ask until the driver has
      // actually visited and left it.
      void CarPlayBridge.stopPendingRecommend({
        id: String(data.destinationId ?? ""), name: dest.name, lat: coords[1], lng: coords[0],
      }).catch(() => {});
      window.dispatchEvent(new CustomEvent("pleasant:car-add-stop", {
        detail: { name: dest.name, coords },
      }));
    })();
  } else if (type === "selectAndStart") {
    // Route picked — stop streaming choice updates to the car.
    carAwaitingChoices = false;
    carLastChoicesSig = "";
    window.dispatchEvent(new CustomEvent("pleasant:car-select-start", {
      detail: { classification: String(data.classification ?? "pleasant") },
    }));
  } else if (type === "end") {
    carAwaitingChoices = false;
    carLastChoicesSig = "";
    window.dispatchEvent(new CustomEvent("pleasant:car-end"));
  } else if (type === "bail") {
    console.log("PLEASANT BAIL: bridge received, dispatching event");
    window.dispatchEvent(new CustomEvent("pleasant:car-bail"));
  } else if (type === "ferryContinue") {
    window.dispatchEvent(new CustomEvent("pleasant:car-ferry-choice", { detail: { choice: "continue" } }));
  } else if (type === "ferryReroute") {
    window.dispatchEvent(new CustomEvent("pleasant:car-ferry-choice", { detail: { choice: "reroute" } }));
  } else if (type === "tripFeedback") {
    // Spoken "what went wrong?" after a Not-pleasant CarPlay arrival rating
    // (Morgan, Aug 6). Mailed via submit-feedback with whatever trip context
    // survives arrival — same inbox as the Feedback button.
    void (async () => {
      const text = String(data.text ?? "").trim();
      if (!text) return;
      try {
        let tripName = "";
        try {
          const t = JSON.parse(localStorage.getItem(ACTIVE_TRIP_KEY) ?? "null") as { name?: string } | null;
          tripName = t?.name ?? "";
        } catch { /* fine */ }
        const { supabase } = await import("@/integrations/supabase/client");
        let email: string | null = null;
        try {
          const { data: sess } = await supabase.auth.getSession();
          email = sess.session?.user?.email ?? null;
        } catch { /* anonymous is fine */ }
        await supabase.functions.invoke("submit-feedback", {
          body: {
            message:
              `[CarPlay voice — after a "Not pleasant" trip rating` +
              (tripName ? ` on "${tripName}"` : "") +
              `]\n\n"${text}"`,
            email,
            page: "/carplay",
            userAgent: null,
          },
        });
        console.log(`[feedback] carplay why sent (${text.length} chars)`);
      } catch {
        console.log("[feedback] carplay why failed — network");
      }
    })();
  } else if (type === "recommendStop") {
    // ★ from the car (Morgan, Aug 5): promote the just-added stop into the
    // community pipeline — same signal as a thumbs-up in the phone panel.
    void (async () => {
      const id = String(data.id ?? "");
      const d = carDestCache.get(id);
      if (!d?.coords) { console.log(`[stops] recommend miss — no cached coords for ${id}`); return; }
      try {
        const { voteOsmPlace } = await import("@/api/nominatedStops");
        const { stopId } = await voteOsmPlace({ id, name: d.name, center: d.coords }, 1);
        if (stopId) {
          console.log(`[stops] recommended ${d.name} → ${stopId}`);
          carplayAnnounce("Recommended — the Pleasant community will see it.");
        } else {
          // Almost always auth: recommendations ride the same account rules
          // as tagging (Morgan, Aug 5).
          carplayAnnounce("Sign in on your phone first, then star it again.");
        }
      } catch {
        console.log("[stops] recommend failed — network");
      }
    })();
  } else if (type === "toggleMute") {
    window.dispatchEvent(new CustomEvent("pleasant:car-mute"));
  } else if (type === "tag") {
    window.dispatchEvent(new CustomEvent("pleasant:car-tag", { detail: { kind: String(data.kind ?? "") } }));
  } else if (type === "rate") {
    // Arrival rating from the car's one-tap panel. The local list stays (it
    // feeds the trip recap), but the rating now ALSO joins route_feedback —
    // this used to stop at localStorage, so a driver could rate every trip
    // and the dashboard would show nothing (Morgan, Aug 13: "i just routed
    // and said it was good at the end and nothing appears on the dashboard").
    try {
      const stars = Number(data.stars ?? 0);
      const cur = JSON.parse(localStorage.getItem(CAR_RATINGS_KEY) ?? "[]") as Array<{ t: number; stars: number }>;
      cur.push({ t: Date.now(), stars });
      localStorage.setItem(CAR_RATINGS_KEY, JSON.stringify(cur.slice(-100)));
      // Watermark FIRST so the startup flush can never double-send this one.
      localStorage.setItem(CAR_RATINGS_FLUSHED_KEY, String(Date.now()));
      submitCarRating(stars);
    } catch { /* best effort */ }
  }
}

if (isIosEarly()) {
  // Ratings stranded by the old localStorage-only car rating path.
  flushUnsentCarRatings();
  // Map identity for the car screen: the SAME Mapbox style the phone uses,
  // as raster tiles the native MKMapView can draw. Token comes from the
  // app's own env (never native source). Apple's map remains the fallback
  // beneath — a failed tile shows Apple's roads, never a blank screen.
  // Sent at launch AND on every reconcile tick: the one-shot send lost the
  // app-startup race in Morgan's Bronco (same disease as the heartbeat) and
  // the car silently stayed on Apple's cartography.
  const sendMapConfig = () => {
    // The CAR token is unrestricted (native requests carry no website
    // referer, so the URL-restricted web token refuses them - the confirmed
    // cause of Apple's map showing instead of Pleasant's style).
    const token = (import.meta.env.VITE_MAPBOX_CAR_TOKEN as string)
      || (import.meta.env.VITE_MAPBOX_TOKEN as string) || "";
    if (!token) return;
    void CarPlayBridge.setMapConfig({
      rasterTileURLTemplate:
        `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${token}`,
      attribution: "© Mapbox © OpenStreetMap",
    }).catch(() => {});
  };
  sendMapConfig();
  void CarPlayBridge.addListener("carRequest", handleCarRequest).catch(() => {});
  // Native location pump (v2: Swift owns location during navigation).
  // Full-rate CLLocationManager fixes, immune to locked-screen throttling.
  // NavigationView subscribes to this event and prefers these fixes.
  void CarPlayBridge.addListener("nativeFix", (data) => {
    // Warm the search-proximity cache from the pump's stream — free and
    // always current while a car is connected (pump runs the whole time).
    const lat = Number(data?.lat), lng = Number(data?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      cachedProximity = { coords: [lng, lat], ts: Date.now() };
      // Motion cache for anticipated-origin routing (Morgan: "anticipate
      // where they will be on the same road in 30-45 seconds").
      const spd = Number(data?.speed);
      const hdg = Number(data?.heading);
      lastMotion = {
        speedMps: Number.isFinite(spd) && spd > 0 ? spd : 0,
        headingDeg: Number.isFinite(hdg) && hdg >= 0 ? hdg : null,
        ts: Date.now(),
      };
    }
    window.dispatchEvent(new CustomEvent("pleasant:native-fix", { detail: data }));
  }).catch(() => {});
  void CarPlayBridge.addListener("carConnected", (data) => {
    carConnected = data?.connected === true;
    if (carConnected) startHeartbeat();
    else stopHeartbeat();
    // Connecting counts as having driven: the phone's CarPlay card may show
    // on the next calm launch (PLE-13, utils/carplayCardGate).
    if (carConnected) markCarplayCardEligible();
    // Interrupted-trip resume: car reattached, a live trip was recorded
    // less than 2h ago, and nothing cleared it (a clean End/arrival does)
    // → re-run the route search so the panel offers the trip back. GO or
    // Back — the driver decides.
    if (carConnected && !resumeOffered) {
      resumeOffered = true;
      try {
        const raw = localStorage.getItem(ACTIVE_TRIP_KEY);
        if (raw) {
          const t = JSON.parse(raw) as { name?: string; end?: LngLat; ts?: number };
          if (t?.end && Date.now() - (t.ts ?? 0) < 2 * 3600_000) {
            setTimeout(() => {
              // Never interrupt: not a drive in progress (cable wiggle
              // reconnects mid-drive!), not a flow the driver just started.
              if (carNavActive || carAwaitingChoices) return;
              window.dispatchEvent(new CustomEvent("pleasant:car-set-destination", {
                detail: { name: t.name ?? "Resume trip", place: "Resuming your trip", coords: t.end },
              }));
            }, 3000);
          } else {
            carplayClearActiveTrip();
          }
        }
      } catch { /* best effort */ }
    }
    window.dispatchEvent(new CustomEvent("pleasant:carplay-connected", { detail: { connected: carConnected } }));
  }).catch(() => {});
  // Reconciliation poll — the race-proof net under the event listener.
  // Morgan's first Stage 0 drive showed "JS ♥ —" while nav ticked at 1s:
  // the WebView was alive but the connect EVENT lost a startup race, so the
  // heartbeat never started and the gate couldn't measure. Every 5s, ask
  // natively and reconcile. Boring, unlosable.
  const reconcile = () => {
    sendMapConfig();
    void CarPlayBridge.isCarConnected().then((r) => {
      const c = r?.connected === true;
      if (c !== carConnected) {
        carConnected = c;
        window.dispatchEvent(new CustomEvent("pleasant:carplay-connected", { detail: { connected: c } }));
      }
      if (c) startHeartbeat();
      else stopHeartbeat();
    }).catch(() => {});
  };
  reconcile();
  setInterval(reconcile, 5_000);
}

function isIosEarly(): boolean {
  try { return Capacitor.getPlatform() === "ios"; } catch { return false; }
}

const isIos = (): boolean => {
  try { return Capacitor.getPlatform() === "ios"; } catch { return false; }
};

const flatten = (coords: LngLat[]): number[] => {
  const out: number[] = new Array(coords.length * 2);
  for (let i = 0; i < coords.length; i++) {
    out[i * 2] = coords[i][0];
    out[i * 2 + 1] = coords[i][1];
  }
  return out;
};

/** SF Symbol for the car screen. Structured maneuver fields first (same
 *  desync fix as the phone banner — see utils/maneuverIcon.ts), prose as
 *  fallback. Coarse on purpose — the banner text carries the detail. */
export function maneuverSymbol(step: {
  instruction?: string;
  maneuverType?: string;
  maneuverModifier?: string;
}): string {
  const type = (step.maneuverType ?? "").toLowerCase();
  const mod = (step.maneuverModifier ?? "").toLowerCase();
  if (type || mod) {
    if (type === "arrive") return "mappin.circle";
    if (mod.includes("uturn")) return "arrow.uturn.down";
    if (type.includes("roundabout") || type.includes("rotary")) return "arrow.triangle.circlepath";
    if (type === "merge") return "arrow.merge";
    if (type === "off ramp" || type === "on ramp" || type === "fork") {
      return mod.includes("left") ? "arrow.up.left" : "arrow.up.right";
    }
    if (mod.includes("left")) return "arrow.turn.up.left";
    if (mod.includes("right")) return "arrow.turn.up.right";
    if (type === "continue" || type === "depart" || type === "new name") return "arrow.up";
  }
  const t = (step.instruction ?? "").toLowerCase();
  if (t.includes("roundabout") || t.includes("rotary")) return "arrow.triangle.circlepath";
  if (t.includes("u-turn")) return "arrow.uturn.down";
  if (t.includes("merge")) return "arrow.merge";
  if (t.includes("exit")) return "arrow.up.right";
  if (t.includes("arrive") || t.includes("destination")) return "mappin.circle";
  if (t.includes("left")) return "arrow.turn.up.left";
  if (t.includes("right")) return "arrow.turn.up.right";
  return "arrow.up";
}

let lastProgressAt = 0;

const ACTIVE_TRIP_KEY = "pleasant.activeCarTrip.v1";
/** True while a navigation session is live (JS-side view). */
let carNavActive = false;

/** Forget the interrupted-trip memory (drive ended properly). */
export function carplayClearActiveTrip(): void {
  try { localStorage.removeItem(ACTIVE_TRIP_KEY); } catch { /* ok */ }
}

/** THE LOVED-ROAD MOMENT (Morgan, Aug 5): gold-flash the stretch under the
 *  car on the CarPlay map. The spoken line travels separately via
 *  carplayAnnounce, so head units without the visual still get the moment. */
export function carplayLovedRoad(): void {
  if (!isIos()) return;
  void CarPlayBridge.lovedRoad().catch(() => {});
}

/** Live nav geometry, kept for detour-cost stamps on mid-drive category
 *  results (Morgan, Aug 6: "for coffee gas and groceries we want to know
 *  how many extra minutes/miles going would add"). */
let navRouteCoords: LngLat[] | null = null;
/** Length of the last navigated route, in metres — survives nav teardown
 *  because the arrival rating fires AFTER the session ends. Feeds the
 *  dashboard's "which route lengths rate well" card (Morgan, Aug 13). */
let lastNavDistanceM: number | null = null;
/** Endpoints of the last navigated route - same teardown-survival reasoning
 *  as lastNavDistanceM. Without these the car arrival rating reached the
 *  dashboard with no location at all: unmappable, unlinkable, and on Aug 18
 *  mislabeled "no location" alongside old phone bugs. A CarPlay drive knows
 *  exactly where it went; the rating should say so. */
let lastNavStart: LngLat | null = null;
let lastNavEnd: LngLat | null = null;
/** Which card the driver was on - "pleasant" | "fastest" | "scenic". Same
 *  survival rule. Without it every car rating reads as a dash in the
 *  dashboard's card column (Morgan, Aug 19: "i want it to say right here
 *  what kind of route it is"). */
let lastNavClassification: string | null = null;
/** Snapshot of the drive that just ENDED, frozen at carplayEndNav. The
 *  arrival rating fires AFTER the session tears down, and the car's rating
 *  panel lives for 45 seconds — long enough for the driver to start the next
 *  trip on the phone. Reading live lastNav* at tap time then files drive A's
 *  rating with drive B's route, card and distance (Aug 19 audit, A2). The
 *  stash is cleared when a NEW drive starts, so a rating tapped mid-drive
 *  still reads the live state for the drive it belongs to. */
let completedNav: {
  start: LngLat | null;
  end: LngLat | null;
  classification: string | null;
  distanceM: number | null;
} | null = null;
function rememberNavDistance(coords: LngLat[]): void {
  try {
    let m = 0;
    for (let i = 1; i < coords.length; i++) {
      const dLat = (coords[i][1] - coords[i - 1][1]) * 111_320;
      const dLng = (coords[i][0] - coords[i - 1][0]) * 111_320 *
        Math.cos((coords[i][1] * Math.PI) / 180);
      m += Math.sqrt(dLat * dLat + dLng * dLng);
    }
    if (m > 0) lastNavDistanceM = Math.round(m);
  } catch { /* stays null */ }
}

export function carplayStartNav(destinationName: string, pleasantReason: string, coordinates: LngLat[], classification?: string | null): void {
  if (!isIos()) return;
  lastProgressAt = 0;
  carNavActive = true;
  navRouteCoords = coordinates;
  rememberNavDistance(coordinates);
  lastNavStart = coordinates[0] ?? null;
  lastNavEnd = coordinates[coordinates.length - 1] ?? null;
  lastNavClassification = classification ?? null;
  // A new drive makes the previous drive's stash stale for any rating tapped
  // from HERE on — mid-drive taps must read this drive, not the last one.
  completedNav = null;
  // RESUME INSURANCE (Morgan's batch): remember the live trip so a crash /
  // phone reboot mid-drive can offer to pick it back up.
  try {
    const endCoord = coordinates[coordinates.length - 1];
    if (endCoord) {
      localStorage.setItem(ACTIVE_TRIP_KEY, JSON.stringify({
        name: destinationName, end: endCoord, ts: Date.now(),
      }));
    }
  } catch { /* best effort */ }
  void CarPlayBridge.startNavigation({
    destinationName,
    pleasantReason,
    coordinates: flatten(coordinates),
  }).catch(() => {});
}

export function carplayUpdateProgress(p: {
  instruction: string;
  maneuverType?: string;
  maneuverModifier?: string;
  distanceToManeuverM: number;
  remainingMeters: number;
  remainingSeconds: number;
}): void {
  if (!isIos()) return;
  // The nav tick fires on every GPS fix; once a second is plenty for a
  // glanceable car banner and keeps bridge traffic negligible.
  const now = Date.now();
  if (now - lastProgressAt < 1000) return;
  lastProgressAt = now;
  // FUTURE TENSE UNTIL IT'S TRUE (Morgan's dashboard photo, Aug 6: the car
  // banner read "3.6 mi — You have arrived at your destination"). Mapbox
  // writes the arrive step in the present; while the maneuver is still
  // ahead, the banner must not claim it happened.
  let instruction = simplifyRoadName(p.instruction);
  if (p.distanceToManeuverM > 50) {
    instruction = instruction.replace(/^you have arrived/i, "You will arrive");
  }
  void CarPlayBridge.updateProgress({
    instruction,
    distanceToManeuverM: p.distanceToManeuverM,
    symbolName: maneuverSymbol(p),
    remainingMeters: p.remainingMeters,
    remainingSeconds: p.remainingSeconds,
  }).catch(() => {});
}

export function carplayUpdateRoute(coordinates: LngLat[], label?: string, classification?: string | null): void {
  if (!isIos()) return;
  navRouteCoords = coordinates;
  rememberNavDistance(coordinates);
  // Mid-drive reroutes keep the rating honest about where the drive ENDED -
  // the arrival rating uses whichever geometry navigated last.
  lastNavStart = lastNavStart ?? coordinates[0] ?? null;
  lastNavEnd = coordinates[coordinates.length - 1] ?? null;
  // "Take fastest" swaps the whole card mid-drive. Without this, the arrival
  // rating still credited the ABANDONED card - a highway drive filed under
  // Scenic, inflating exactly the per-card satisfaction numbers the scoring
  // work depends on (Aug 19 audit, A1). Undefined = caller didn't say, keep
  // what we had; null/string = explicit.
  if (classification !== undefined) lastNavClassification = classification;
  void CarPlayBridge.updateRoute({
    coordinates: flatten(coordinates),
    ...(label ? { label } : {}),
  }).catch(() => {});
}

/** Arrival celebration for the car screen (Morgan: the recap moment
 *  belongs on the big screen too). Painted on the map canvas — the car is
 *  stationary at arrival — plus a one-tap "how pleasant?" panel. */
export function carplayArrived(summary: string): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.arrived({ summary }).catch(() => {});
}

/** Native arrival chime (Morgan, Aug 7: "there is no arrive chime").
 *  WebAudio dies when the phone locks — exactly a real drive's end state.
 *  Returns true when the native path took it (web callers keep their own
 *  synth as the fallback). */
/** FERRY POPUP ON THE DASH (Morgan, Aug 13: "a pop up ... on the car that
 *  says this route has a ferry crossing, continue or choose new route").
 *  Presents a two-button CPNavigationAlert on the car screen; the driver's
 *  tap comes back as the pleasant:car-ferry-choice window event. */
export function carplayFerryAlert(title: string): void {
  if (!isIosEarly()) return;
  void CarPlayBridge.showFerryAlert({ title }).catch(() => {});
}

export function carplayChime(): boolean {
  if (!isIos()) return false;
  void CarPlayBridge.chime().catch(() => {});
  return true;
}

export function carplayEndNav(): void {
  carNavActive = false;
  navRouteCoords = null; // detour-cost stamps end with the trip
  // Freeze this drive's identity for the arrival rating that hasn't been
  // tapped yet (see completedNav above). lastNav* stay live for any rating
  // that beats this call in the race.
  completedNav = {
    start: lastNavStart,
    end: lastNavEnd,
    classification: lastNavClassification,
    distanceM: lastNavDistanceM,
  };
  carplayClearActiveTrip(); // clean end — nothing to resume
  if (!isIos()) return;
  void CarPlayBridge.endNavigation().catch(() => {});
}
